import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type Router,
} from '@tanstack/react-router';
import type { RouterHistory } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { DepositPage } from '@/app/customer/deposit/deposit-page';
import { HistoryPage } from '@/app/customer/history/history-page';
import { WithdrawalPage } from '@/app/customer/withdrawal/withdrawal-page';
import { LoginPage } from '@/app/login/login-page';
import { AbandonmentPage } from '@/app/operator/abandonment/abandonment-page';
import { AdminPage } from '@/app/operator/admin/admin-page';
import { AiReviewPage } from '@/app/operator/ai-review/ai-review-page';
import { ClearingPage } from '@/app/operator/clearing/clearing-page';
import { DashboardPage } from '@/app/operator/dashboard/dashboard-page';
import { LedgerDivergencesPage } from '@/app/operator/ledger-divergences/ledger-divergences-page';
import { LookupPage } from '@/app/operator/lookup/lookup-page';
import { ManualReviewPage } from '@/app/operator/manual-review/manual-review-page';
import { OperatorsPage } from '@/app/operator/operators/operators-page';
import { ReviewQueuePage } from '@/app/operator/review-queue/review-queue-page';
import { WithdrawalsPage } from '@/app/operator/withdrawals/withdrawals-page';
import { HistoryPage as OpsHistoryPage } from '@/app/operator/history/history-page';
import { TransactionDetailPage } from '@/app/operator/history/transaction-detail-page';
import { SettingsPage } from '@/app/operator/settings/settings-page';
import { validateReturnSearch } from '@/lib/nav/return-to';
import { CustomerShell, OperatorShell } from '@/components/layout/shells';
import { EmptyState } from '@/components/ui/feedback';
import { RequireArea } from './guards';

/**
 * The route tree.
 *
 * Code-based rather than file-based, so the two areas are visible as two subtrees in one place —
 * which is exactly the property D-I7 asks a reader to be able to verify. `/app/*` is the
 * participating website's cashier; `/ops/*` is PaymentFlow's console. Each subtree wraps its
 * pages in its own shell *and* its own area guard, so a page cannot be added to the wrong area by
 * accident: it would inherit the wrong chrome and the wrong guard together.
 */

const rootRoute = createRootRoute({
  component: function RootLayout(): ReactNode {
    return <Outlet />;
  },
  notFoundComponent: function NotFound(): ReactNode {
    return (
      <div className="flex min-h-dvh items-center justify-center p-6">
        <EmptyState
          title="That page does not exist"
          description="The link may be out of date, or the transaction it pointed at may have been in a different environment. Use the navigation to get back to a known screen."
          className="max-w-lg"
        />
      </div>
    );
  },
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // The area guard on each subtree does the real work; this just picks a starting point.
    // TanStack Router's `redirect` is thrown as control flow, not as an error — the router
    // catches it by shape, so `only-throw-error` does not apply here.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/login' });
  },
  component: function Index(): ReactNode {
    return null;
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// --- Customer area (the participating website's UI) -------------------------------------------

const customerRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'customer',
  component: function CustomerLayout(): ReactNode {
    return (
      <RequireArea area="customer">
        <CustomerShell>
          <Outlet />
        </CustomerShell>
      </RequireArea>
    );
  },
});

const depositRoute = createRoute({
  getParentRoute: () => customerRoute,
  path: '/app/deposit',
  component: DepositPage,
});

const withdrawRoute = createRoute({
  getParentRoute: () => customerRoute,
  path: '/app/withdraw',
  component: WithdrawalPage,
});

const historyRoute = createRoute({
  getParentRoute: () => customerRoute,
  path: '/app/history',
  component: HistoryPage,
});

// --- Operator area (PaymentFlow's console) ----------------------------------------------------

const operatorRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'operator',
  component: function OperatorLayout(): ReactNode {
    return (
      <RequireArea area="operator">
        <OperatorShell>
          <Outlet />
        </OperatorShell>
      </RequireArea>
    );
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/dashboard',
  component: DashboardPage,
});

const lookupRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/lookup',
  component: LookupPage,
});

const aiReviewRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/ai-review',
  component: AiReviewPage,
});

const reviewQueueRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/reviews',
  component: ReviewQueuePage,
});

// The two detail routes accept a return address. `validateReturnSearch` is the gate: it keeps
// `from` only when it names a list screen this app returns to, and keeps it canonicalised, so a
// component never sees a `from` it would have to distrust.
const manualReviewRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/reviews/$caseId',
  component: ManualReviewPage,
  validateSearch: validateReturnSearch,
});

const adminRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/admin',
  component: AdminPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/settings',
  component: SettingsPage,
});

const clearingRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/clearing',
  component: ClearingPage,
});

const operatorsRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/operators',
  component: OperatorsPage,
});

const opsWithdrawalsRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/withdrawals',
  component: WithdrawalsPage,
});

const opsAbandonmentRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/abandonment',
  component: AbandonmentPage,
});

const opsHistoryRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/history',
  component: OpsHistoryPage,
});

const opsTransactionDetailRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/history/$transactionId',
  component: TransactionDetailPage,
  validateSearch: validateReturnSearch,
});

const ledgerDivergencesRoute = createRoute({
  getParentRoute: () => operatorRoute,
  path: '/ops/ledger-divergences',
  component: LedgerDivergencesPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  customerRoute.addChildren([depositRoute, withdrawRoute, historyRoute]),
  operatorRoute.addChildren([
    dashboardRoute,
    lookupRoute,
    aiReviewRoute,
    reviewQueueRoute,
    manualReviewRoute,
    adminRoute,
    clearingRoute,
    operatorsRoute,
    settingsRoute,
    opsWithdrawalsRoute,
    opsAbandonmentRoute,
    opsHistoryRoute,
    opsTransactionDetailRoute,
    ledgerDivergencesRoute,
  ]),
]);

/** Build a router. Tests pass a memory history to mount a single route in isolation. */
export function buildRouter(history?: RouterHistory): Router<typeof routeTree, 'never', true> {
  // When the app is served under a sub-path (Vite `base`, e.g. `/wallet/`), the router must strip
  // that prefix so `/wallet/app/deposit` matches the `/app/deposit` route. `import.meta.env.BASE_URL`
  // is `/` for a root/subdomain deploy, which we leave unset so nothing changes there.
  const basepath = import.meta.env.BASE_URL.replace(/\/$/, '');
  return createRouter({
    routeTree,
    defaultPreload: 'intent',
    scrollRestoration: true,
    ...(basepath ? { basepath } : {}),
    ...(history ? { history } : {}),
  }) as Router<typeof routeTree, 'never', true>;
}

/** The application router. */
export const router = buildRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
